import { useCallback, useEffect, useState } from 'react';
import type { Formula, FormulaDraft, ReminderSettings } from '@domain/entities';
import { useContainer } from '../providers/container-context';
import { useToast } from '../providers/toast-context';
import { useAuth } from '../providers/auth-context';
import { useAsync } from '../hooks/use-async';
import { useRealtimeVersion } from '../providers/realtime-context';
import { useIsMobile } from '../hooks/use-is-mobile';
import { ConfirmModal, ErrorState, Modal, RefreshButton, TableSkeleton, TextField } from '../components/ui';
import { formatFcfa } from '../theme/tokens';

const NEW_DRAFT: FormulaDraft = { slug: '', label: '', amountXof: 0, durationDays: 30 };

export function FormulasPage(): JSX.Element {
  const container = useContainer();
  const toast = useToast();
  const { can } = useAuth();
  const vFormulas = useRealtimeVersion('formulas');

  const isMobile = useIsMobile();
  const formulas = useAsync(() => container.settings.listFormulas.execute(), [container, vFormulas]);
  const reminderResource = useAsync(() => container.settings.getReminder.execute(), [container]);

  const [reminder, setReminder] = useState<ReminderSettings | null>(null);
  const [editorId, setEditorId] = useState<string | null | undefined>(undefined);
  const [draft, setDraft] = useState<FormulaDraft>(NEW_DRAFT);
  const [saving, setSaving] = useState(false);
  const [pending, setPending] = useState<{ title: string; body: string; confirmLabel?: string; danger?: boolean; fn: () => void } | null>(null);
  const [actionBusy, setActionBusy] = useState(false);

  useEffect(() => {
    if (reminderResource.data) setReminder(reminderResource.data);
  }, [reminderResource.data]);

  const persistReminder = useCallback(
    async (next: ReminderSettings) => {
      setReminder(next);
      try {
        await container.settings.saveReminder.execute(next);
      } catch (error) {
        toast.notifyError(error);
        reminderResource.reload();
      }
    },
    [container, toast, reminderResource],
  );

  const openEditor = useCallback((formula: Formula | null) => {
    if (formula === null) {
      setEditorId(null);
      setDraft(NEW_DRAFT);
      return;
    }
    setEditorId(formula.id);
    setDraft({
      slug: formula.slug,
      label: formula.label,
      description: formula.description,
      amountXof: formula.amountXof,
      durationDays: formula.durationDays,
    });
  }, []);

  const save = useCallback(async () => {
    if (editorId === undefined) return;
    setSaving(true);
    try {
      await container.settings.saveFormula.execute({ id: editorId, draft });
      toast.notify(editorId === null ? 'Formule créée' : 'Formule mise à jour');
      setEditorId(undefined);
      formulas.reload();
    } catch (error) {
      toast.notifyError(error);
    } finally {
      setSaving(false);
    }
  }, [container, editorId, draft, toast, formulas]);

  const remove = useCallback(
    async (id: string) => {
      try {
        await container.settings.deleteFormula.execute(id);
        toast.notify('Formule supprimée');
        formulas.reload();
      } catch (error) {
        toast.notifyError(error);
      }
    },
    [container, toast, formulas],
  );

  const rows = formulas.data ?? [];

  return (
    <>
      {reminder ? (
        <section className="bbm-card" style={{ borderRadius: 14, marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 10 }}>
            <button
              type="button"
              role="switch"
              aria-checked={reminder.enabled}
              aria-label="Relance automatique à échéance"
              disabled={!can('formulas', 'edit')}
              onClick={() => void persistReminder({ ...reminder, enabled: !reminder.enabled })}
              style={{
                width: 44,
                height: 26,
                borderRadius: 99,
                border: 'none',
                background: reminder.enabled ? '#5CA989' : '#DDE2E7',
                position: 'relative',
                flexShrink: 0,
              }}
            >
              <span
                style={{
                  width: 20,
                  height: 20,
                  borderRadius: 99,
                  background: '#fff',
                  position: 'absolute',
                  top: 3,
                  left: reminder.enabled ? 21 : 3,
                  transition: 'left 0.15s',
                }}
              />
            </button>
            <div style={{ fontFamily: "'Baloo 2', sans-serif", fontSize: 15, fontWeight: 700 }}>
              Relance automatique à échéance
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
            <span style={{ fontSize: 13, color: '#4A5560' }}>Envoyer</span>
            <input
              type="number"
              min={1}
              max={90}
              aria-label="Jours avant échéance"
              className="bbm-field"
              style={{ width: 76, marginBottom: 0, padding: '7px 9px' }}
              value={reminder.daysBefore}
              disabled={!can('formulas', 'edit')}
              onChange={(event) =>
                setReminder({ ...reminder, daysBefore: Number(event.target.value) || 1 })
              }
              onBlur={() => void persistReminder(reminder)}
            />
            <span style={{ fontSize: 13, color: '#4A5560' }}>
              jours avant l'échéance de la formule souscrite
            </span>
          </div>

          <label className="bbm-label" htmlFor="reminder-template">
            Message envoyé à l'entreprise
          </label>
          <textarea
            id="reminder-template"
            className="bbm-field"
            rows={3}
            style={{ maxWidth: 640, resize: 'vertical' }}
            value={reminder.template}
            disabled={!can('formulas', 'edit')}
            onChange={(event) => setReminder({ ...reminder, template: event.target.value })}
            onBlur={() => void persistReminder(reminder)}
          />
          <div style={{ fontSize: 11.5, color: '#8A95A3', marginTop: 6 }}>
            Variables : {'{entreprise}'}, {'{formule}'}, {'{jours}'}
          </div>
        </section>
      ) : null}

      <div className="bbm-toolbar">
        <div className="bbm-toolbar__start">
          <div className="bbm-toolbar__hint">{rows.length} formule(s) tarifaire(s)</div>
        </div>
        <div className="bbm-toolbar__end">
          <button
            type="button"
            className="bbm-btn bbm-btn--primary"
            disabled={!can('formulas', 'create')}
            title={can('formulas', 'create') ? '' : 'Réservé au super-admin'}
            onClick={() => openEditor(null)}
          >
            + Nouvelle formule
          </button>
          <RefreshButton onRefresh={formulas.reload} loading={formulas.loading} />
        </div>
      </div>

      {formulas.loading ? <TableSkeleton rows={3} /> : null}
      {formulas.error ? (
        <ErrorState
          status={formulas.error.status}
          message={formulas.error.message}
          onRetry={formulas.reload}
        />
      ) : null}

      {rows.length > 0 ? (
        isMobile ? (
          <div className="bbm-card-stack">
            {rows.map((formula) => (
              <div key={formula.id} className="bbm-mobile-card">
                <div className="bbm-mobile-card__head">
                  <div>
                    <div className="bbm-mobile-card__title">{formula.label}</div>
                    <div className="bbm-mobile-card__sub">{formatFcfa(formula.amountXof)}</div>
                  </div>
                </div>
                <div className="bbm-mobile-card__fields">
                  <div className="bbm-mobile-card__field">
                    <span className="bbm-mobile-card__label">Durée</span>
                    <span>{Math.round(formula.durationDays / 30)} mois</span>
                  </div>
                </div>
                <div className="bbm-mobile-card__actions">
                  <button type="button" className="bbm-btn" disabled={!can('formulas', 'edit')} onClick={() => openEditor(formula)}>Modifier</button>
                  <button
                    type="button"
                    className="bbm-btn bbm-btn--danger"
                    disabled={!can('formulas', 'delete')}
                    title={can('formulas', 'delete') ? '' : 'Réservé au super-admin'}
                    onClick={() => setPending({
                      title: 'Supprimer cette formule ?',
                      body: 'Cette action est irréversible.',
                      confirmLabel: 'Supprimer',
                      danger: true,
                      fn: () => void remove(formula.id),
                    })}
                  >Supprimer</button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bbm-panel">
            <div className="bbm-row bbm-row--head">
              <div style={{ width: 280 }}>Nom</div>
              <div style={{ width: 150 }}>Prix</div>
              <div style={{ width: 120 }}>Durée</div>
              <div style={{ marginLeft: 'auto' }}>Actions</div>
            </div>
            {rows.map((formula) => (
              <div key={formula.id} className="bbm-row">
                <div className="bbm-cell-strong" style={{ width: 280 }}>
                  {formula.label}
                </div>
                <div style={{ width: 150, fontSize: 13.5, fontWeight: 800, color: '#33506B' }}>
                  {formatFcfa(formula.amountXof)}
                </div>
                <div className="bbm-cell" style={{ width: 120 }}>
                  {Math.round(formula.durationDays / 30)} mois
                </div>
                <div className="bbm-row__actions">
                  <button
                    type="button"
                    className="bbm-btn"
                    disabled={!can('formulas', 'edit')}
                    onClick={() => openEditor(formula)}
                  >
                    Modifier
                  </button>
                  <button
                    type="button"
                    className="bbm-btn bbm-btn--danger"
                    disabled={!can('formulas', 'delete')}
                    title={can('formulas', 'delete') ? '' : 'Réservé au super-admin'}
                    onClick={() => setPending({
                      title: 'Supprimer cette formule ?',
                      body: 'Cette action est irréversible.',
                      confirmLabel: 'Supprimer',
                      danger: true,
                      fn: () => void remove(formula.id),
                    })}
                  >
                    Supprimer
                  </button>
                </div>
              </div>
            ))}
          </div>
        )
      ) : null}

      {editorId !== undefined ? (
        <Modal
          title={editorId === null ? 'Nouvelle formule' : 'Modifier la formule'}
          width={480}
          onClose={() => setEditorId(undefined)}
          onSubmit={() => void save()}
          busy={saving}
        >
          <TextField
            label="Libellé"
            value={draft.label}
            onChange={(value) => setDraft({ ...draft, label: value })}
          />
          <TextField
            label="Slug (lettres minuscules, tirets)"
            value={draft.slug}
            onChange={(value) => setDraft({ ...draft, slug: value })}
          />
          <TextField
            label="Prix (FCFA)"
            type="number"
            value={draft.amountXof}
            onChange={(value) => setDraft({ ...draft, amountXof: Number(value) || 0 })}
          />
          <TextField
            label="Durée (jours)"
            type="number"
            value={draft.durationDays}
            onChange={(value) => setDraft({ ...draft, durationDays: Number(value) || 30 })}
          />
        </Modal>
      ) : null}
      {pending !== null ? (
        <ConfirmModal
          title={pending.title}
          body={pending.body}
          confirmLabel={pending.confirmLabel}
          danger={pending.danger}
          busy={actionBusy}
          onConfirm={() => {
            setActionBusy(true);
            Promise.resolve(pending.fn()).finally(() => {
              setActionBusy(false);
              setPending(null);
            });
          }}
          onCancel={() => setPending(null)}
        />
      ) : null}
    </>
  );
}
